<template>
  <div style="padding: 1vw">
    <div style="display: flex; justify-content: space-between; align-items: flex-end; padding: 0 1vw">
      <div class="pageTitle">
        <h3 class="fw-bold" style="color: #000; font-size: 1.8rem">
          근로계약서 조회
        </h3>
        <span class="pageDesc">
          병원에서 등록한 근로계약서를 볼 수 있습니다.
        </span>
      </div>
      <div style="display: flex; align-items: center;">
      </div>
    </div>
    <div class="container-wrapper">
      <div class="search-filter-container table-info-filter">
        <!-- 직원명 검색 영역 -->
        <div class="search-filter-row">
          <div class="search-filter-title">
            검색어
          </div>
          <div class="search-filter-content search-keyword-area">
            <select class="search-select" v-model="searchForm.filter">
              <option value="">전체</option>
              <option value="name">직원명</option>
              <option value="phone">전화번호</option>
              <option value="hospitalName">병원명</option>
            </select>
            <div class="search-input-container">
              <div class="search-icon">
                <i class="fa-regular fa-magnifying-glass"/>
              </div>
              <a-input
                  style="width: 220px"
                  type="text"
                  class="search-input-text"
                  v-model:value="searchForm.keyword"
                  placeholder="검색어를 입력하세요"
                  @pressEnter="handleSearch"
              >
              </a-input>
            </div>
            <a-button
                class="search-btn-filter"
                @click="handleSearch"
            >
              검색
            </a-button>
            <a-button
                class="init-btn"
                @click="handleReset"
            >
              초기화
            </a-button>
          </div>
        </div>

        <!-- 계약상태 선택 영역 -->
        <div class="search-filter-row">
          <div class="search-filter-title">
            계약상태
          </div>
          <div class="search-filter-content">
            <a-select
                v-model:value="searchForm.status"
                style="width: 200px"
                @change="handleSearch"
                placeholder="계약상태 선택"
            >
              <a-select-option :value="null">전체</a-select-option>
              <a-select-option value="CONTRACT_CREATED">계약생성됨</a-select-option>
              <a-select-option value="SIGNATURE_PARTICIPATED">서명진행중</a-select-option>
              <a-select-option value="SIGNATURE_CONCLUDED">서명완료</a-select-option>
            </a-select>
          </div>
        </div>

        <!-- 계약일자 선택 영역 -->
        <div class="search-filter-row">
          <div class="search-filter-title">
            계약일자
          </div>
          <div class="search-filter-content">
            <div class="date-range">
              <a-range-picker
                  v-model:value="dateRange"
                  style="width: 280px"
                  @change="handleDateChange"
                  :placeholder="['시작일', '종료일']"
                  :format="'YYYY-MM-DD'"
                  :allowClear="true"
                  :disabled-date="disabledDate"
                  :locale="locale"
              />
            </div>
          </div>
        </div>
        <div class="search-filter-row">
          <div class="search-filter-title">
            테스트 병원 여부
          </div>
          <div class="search-filter-content">
            <a-checkbox v-model:checked="searchForm.excludeTestHospital">
              테스트 병원 제외
            </a-checkbox>
          </div>
        </div>
      </div>
    </div>
    <div class="container-wrapper">
      <div class="col-xs-12 table-info-filter">
        <div class="total-div">
          <p>
            전체 <span>{{ formatCommaNumber(total) }}</span>건
          </p>
        </div>
        <div class="search-container">
          <a-select
              v-model:value="pageSize"
              style="width: 120px"
              @change="handleSizeChange"
          >
            <a-select-option :value="10">10개씩 보기</a-select-option>
            <a-select-option :value="20">20개씩 보기</a-select-option>
            <a-select-option :value="50">50개씩 보기</a-select-option>
          </a-select>
        </div>
      </div>
    </div>
    <div class="container-wrapper">
      <a-tabs 
        v-model:activeKey="activeTab" 
        @change="handleTabChange"
        class="custom-tabs"
      >
        <a-tab-pane key="WORKING" :tab="'재직자'">
          <a-table
              :loading="loading"
              :columns="filteredColumns"
              :data-source="filteredContractList"
              :pagination="false"
              :locale="{ emptyText: '데이터가 없습니다.' }"
              row-key="id"
          />
        </a-tab-pane>
        <a-tab-pane key="RESIGNED" :tab="'퇴사자'">
          <a-table
              :loading="loading"
              :columns="filteredColumns"
              :data-source="filteredContractList"
              :pagination="false"
              :locale="{ emptyText: '데이터가 없습니다.' }"
              row-key="id"
          />
        </a-tab-pane>
      </a-tabs>
    </div>
    <pagination 
        :page-info="paginationData"
        page-name="LaborContractList"
        @change-page="paginationHandler"
        v-if="total > 0"
    />
  </div>
</template>
<script>
import { 
  phoneNumberFormatter,
  formatCommaNumber,
} from "@/utils/utils";
import dayjs from 'dayjs';
import locale from 'ant-design-vue/es/date-picker/locale/ko_KR';
import 'dayjs/locale/ko';
import Pagination from "@/views/components/Pagination.vue";
import CardContainer from "@/views/components/CardContainer.vue";
import { QuestionCircleOutlined, SearchOutlined } from '@ant-design/icons-vue';
import {Select, Button, Input, Tabs, DatePicker} from 'ant-design-vue';
import {PrixContractApi} from './api';

export default {
  name: "LaborContracts",
  components: {
    Pagination,
    CardContainer,
    ASelect: Select,
    ASelectOption: Select.Option,
    AButton: Button,
    AInput: Input,
    ARangePicker: DatePicker.RangePicker,
    QuestionCircleOutlined,
    SearchOutlined,
    ATabs: Tabs,
    ATabPane: Tabs.TabPane,
  },
  computed: {
    filteredContractList() {
      return this.contractList;
    },
    columns() {
      let cols = [...this.baseColumns];
      
      // 번호 컬럼 추가
      cols.unshift({
        title: '번호',
        key: 'index',
        width: 70,
        align: 'center',
        customRender: ({index}) => {
          // 현재 페이지를 고려한 번호 계산
          return ((this.current - 1) * this.pageSize) + index + 1;
        }
      });

      // 체크박스 컬럼 추가
      if (this.isCheckboxVisible) {
        cols.unshift({
          title: () => {
            const deleteCount = this.contractList.filter(item => item.isChecked).length;
            return (
              <div>
                <span>{deleteCount}개 선택됨</span><br/>
                <a-checkbox 
                  style={{cursor: "pointer", marginLeft: '8px'}}
                  onChange={(e) => {
                    const checkValue = e.target.checked;
                    this.contractList.forEach(item => {
                      item.isChecked = checkValue;
                    });
                  }}
                />
              </div>
            );
          },
          key: 'selection',
          width: 80,
          customRender: ({record}) => {
            return (
              <a-checkbox 
                checked={record.isChecked}
                onChange={(e) => record.isChecked = e.target.checked}
              />
            );
          },
          align: 'center'
        });
      }

      if (this.activeTab === 'RESIGNED') {
        cols.push(...this.resignedColumns);
      }
      return cols;
    },
    filteredColumns() {
      if (this.activeTab === 'WORKING') {
        return this.columns.filter(col => col.key !== 'resignationDate');
      }
      return this.columns.filter(col => !['status', 'resignationDate'].includes(col.key));
    }
  },
  data() {
    return {
      // 검색 필터 데이터
      searchForm: {
        employmentStatus: null,  // 빈 문자열 대신 null
        status: null,           // 빈 문자열 대신 null
        keyword: '',            // 문자열 검색은 빈 문자열 유지
        filter: '',             // 필터명
        excludeTestHospital: false, // 테스트 병원 제외 여부
      },
      dateRange: null,

      // 페이징 데이터
      current: 1,            // 현재 페이지
      pageSize: 10,          // 페이지당 항목 수
      total: 0,              // 전체 항목 수

      // 계약 목록 데이터
      contractList: [],

      // 로딩 상태
      loading: false,

      // 테이블 컬럼 정의
      baseColumns: [
        {
          title: '병원명',
          dataIndex: 'employer',
          key: 'employer',
          width: 150,
          align: 'center',
          customRender: ({text, record}) => {
            const hospitalName = record.employer?.name
            return (
                <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px'}}>
                  <span>{text ? hospitalName : '-'}</span>
                </div>
            );
          }
        },
        {
          title: '직원명',
          dataIndex: 'employeeName',
          key: 'employeeName',
          width: 120,
          align: 'center'
        },
        {
          title: '연락처',
          dataIndex: 'employeePhone',
          key: 'employeePhone',
          width: 150,
          align: 'center',
          customRender: ({text}) => {
            return (
                <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px'}}>
                  <span>{text ? phoneNumberFormatter(text) : '-'}</span>
                </div>
            );
          }
        },
        {
          dataIndex: 'employmentStatus',
          key: 'employmentStatus',
          width: 120,
          align: 'center',
          title: () => (
            <div style="display: flex; align-items: center; justify-content: center; gap: 4px;">
              근무상태
              <a-tooltip title="계약 생성시 입사예정, 서명이 모두 완료되면 재직중으로 자동 변경됩니다.">
                <QuestionCircleOutlined style={{ color: 'rgba(0, 0, 0, 0.45)', cursor: 'help' }} />
              </a-tooltip>
            </div>
          ),
          customRender: ({text}) => {
            const statusMap = {
              WORKING: {text: '재직중', color: 'success'},
              RESIGNED: {text: '퇴사', color: 'error'},
              ON_LEAVE: {text: '휴직중', color: 'processing'},
              PROBATION: {text: '수습중', color: 'warning'},
              TERMINATED: {text: '계약해지', color: 'error'},
              PENDING: {text: '입사예정', color: 'purple'}
            };
            const status = statusMap[text] || {text: text, class: ''};
            return <a-tag color={status.color}>{status.text}</a-tag>;
          }
        },
        {
          title: () => (
            <div style="display: flex; align-items: center; justify-content: center; gap: 4px;">
              계약상태
              <a-tooltip title="신규작성 완료시 계약생성됨, 서명참여자 1명이 서명하면 서명진행중, 모든 서명이 완료되면 서명완료로 자동 변경됩니다.">
                <QuestionCircleOutlined style={{ color: 'rgba(0, 0, 0, 0.45)', cursor: 'help' }} />
              </a-tooltip>
            </div>
          ),
          dataIndex: 'status',
          key: 'status',
          width: 120,
          align: 'center',
          customRender: ({text}) => {
            const statusMap = {
              CONTRACT_CREATED: {text: '계약생성됨', color: 'default'},
              SIGNATURE_PARTICIPATED: {text: '서명진행중', color: 'warning'},
              SIGNATURE_CONCLUDED: {text: '서명완료', color: 'success'},
            };
            const status = statusMap[text] || {text: text, class: ''};
            return <a-tag color={status.color}>{status.text}</a-tag>;
          }
        },
        {
          dataIndex: 'contractDate',
          key: 'contractDate',
          width: 120,
          align: 'center',
          title: () => (
            <div style="display: flex; align-items: center; justify-content: center; gap: 4px;">
              계약일자
              <a-tooltip title="계약서 하단 계약일자">
                <QuestionCircleOutlined style={{ color: 'rgba(0, 0, 0, 0.45)', cursor: 'help' }} />
              </a-tooltip>
            </div>
          )
        },
        {
          dataIndex: 'contractStartDate',
          key: 'contractStartDate',
          width: 120,
          align: 'center',
          title: () => (
            <div style="display: flex; align-items: center; justify-content: center; gap: 4px;">
              근무시작일
              <a-tooltip title="계약서 계약기간란에 표기한 일자">
                <QuestionCircleOutlined style={{ color: 'rgba(0, 0, 0, 0.45)', cursor: 'help' }} />
              </a-tooltip>
            </div>
          )
        },
        {
          dataIndex: 'createAt',
          key: 'createAt',
          width: 180,
          align: 'center',
          title: () => (
            <div style="display: flex; align-items: center; justify-content: center; gap: 4px;">
              작성일시
              <a-tooltip title="시스템을 통해 계약서를 생성한 시간">
                <QuestionCircleOutlined style={{ color: 'rgba(0, 0, 0, 0.45)', cursor: 'help' }} />
              </a-tooltip>
            </div>
          ),
          customRender: ({text}) => {
            return text ? dayjs(text).format('YYYY-MM-DD HH:mm:ss') : '-';
          }
        },
      ],
      resignedColumns: [
        {
          dataIndex: 'resignationDate',
          key: 'resignationDate',
          width: 120,
          align: 'center',
          title: () => (
            <div style="display: flex; align-items: center; justify-content: center; gap: 4px;">
              퇴사일자
              <a-tooltip title="실제 퇴사 처리된 날짜">
                <QuestionCircleOutlined style={{ color: 'rgba(0, 0, 0, 0.45)', cursor: 'help' }} />
              </a-tooltip>
            </div>
          ),
          customRender: ({text}) => {
            return text || '-';
          }
        },
        {
          dataIndex: 'modifyAt',
          key: 'modifyAt',
          width: 180,
          align: 'center',
          title: () => (
            <div style="display: flex; align-items: center; justify-content: center; gap: 4px;">
              퇴사처리일시
              <a-tooltip title="시스템에서 퇴사처리한 일시">
                <QuestionCircleOutlined style={{ color: 'rgba(0, 0, 0, 0.45)', cursor: 'help' }} />
              </a-tooltip>
            </div>
          ),
          customRender: ({text}) => {
            return text ? dayjs(text).format('YYYY-MM-DD HH:mm:ss') : '-';
          }
        }
      ],
      activeTab: 'WORKING',
      locale,
      dayjs,
      isCheckboxVisible: false,
      paginationData: {
        currentPage: 1,
        totalPages: 0,
      },
    }
  },
  watch: {
    // select box 변경 감지
    'searchForm.employmentStatus'() {
      this.handleSearch();
    },
    'searchForm.status'() {
      this.handleSearch();
    },
    'searchForm.excludeTestHospital'() {
      this.handleSearch();
    },
    // 날짜 변경 감지
    dateRange() {
      this.handleSearch();
    },
  },
  async mounted() {
    this.$nextTick(() => {
      this.fetchContracts();
    });
  },
  methods: {
    formatCommaNumber,
    // API 호출 메서드
    async fetchContracts() {
      try {
        this.loading = true;
        const params = {
          page: this.paginationData.currentPage,
          limit: this.pageSize,
          employmentStatuses: this.activeTab === 'WORKING' ? 
            ['WORKING', 'ON_LEAVE', 'PROBATION', 'PENDING'].join(',') : 
            ['RESIGNED', 'TERMINATED'].join(','),
          status: this.searchForm.status,
          keyword: this.searchForm.keyword,
          startDate: this.dateRange?.[0]?.format('YYYY-MM-DD') || undefined,
          endDate: this.dateRange?.[1]?.format('YYYY-MM-DD') || undefined,
        };
        
        if(this.searchForm.filter) {
          params.filter = this.searchForm.filter;
        }

        if(this.searchForm.excludeTestHospital) {
          params.excludeTestCompany = 'true';
        }
        
        const response = await PrixContractApi.getLaborContracts(params);

        if (response?.data?.items) {
          this.contractList = response.data.items;
          this.total = response.data.total;
          // 페이지네이션 데이터 업데이트
          this.paginationData.totalPages = Math.ceil(this.total / this.pageSize);
        } else {
          this.contractList = [];
          this.total = 0;
          this.paginationData.totalPages = 0;
        }
      } catch (error) {
        console.error('계약 목록 조회 실패:', error);
        this.contractList = [];
        this.total = 0;
        this.paginationData.totalPages = 0;
      } finally {
        this.loading = false;
      }
    },

    // 검색 처리
    handleSearch() {
      if (this.searchForm.keyword) {
        this.searchForm.keyword = this.searchForm.keyword.trim();
      }
      this.paginationData.currentPage = 1;  // 검색 시 첫 페이지로 이동
      this.fetchContracts();
    },

    // 초기화 처리
    handleReset() {
      this.searchForm = {
        employmentStatus: null,
        status: null,
        keyword: '',
        filter: '',
        excludeTestHospital: false,
      };
      this.dateRange = null;
      this.current = 1;
      this.fetchContracts();
    },
    handleTabChange(key) {
      this.activeTab = key;
      this.paginationData.currentPage = 1;  // 페이지만 초기화
      this.fetchContracts();
      console.log('asd')
    },
    handleDateChange(dates) {
      this.dateRange = dates;
      this.handleSearch();
    },
    disabledDate(current) {
      return current && current > dayjs().endOf('day');
    },
    async paginationHandler(childPassParam) {
      this.paginationData.currentPage = childPassParam.page;
      await this.fetchContracts();
    },
    handleSizeChange(value) {
      this.pageSize = value;
      this.paginationData.currentPage = 1; // 페이지 사이즈 변경시 첫 페이지로 이동
      this.fetchContracts();
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/assets/scss/certificates.scss';
@mixin mobile {
  @media screen and (max-width: 768px) {
    @content;
  }
}

.labor-contract-container {
  padding: 20px;

  .search-filter-container {
    background-color: #fff;
    border: 1px solid #e8e8e8;
    border-radius: 4px;
    margin-bottom: 16px;

    .search-filter-row {
      display: flex;
      align-items: center;
      border-bottom: 1px solid #f0f0f0;

      @include mobile {
        flex-direction: column;
        align-items: flex-start;
        gap: 8px;
      }

      &:last-child {
        border-bottom: none;
      }

      .search-filter-title {
        width: 120px;
        padding: 6px 12px;
        background-color: #f7f7f7;
        color: #555;
        font-weight: 500;
        font-size: 14px;
        border-radius: 4px;
        text-align: center;
        margin-right: 16px;

        @include mobile {
          width: 100%;
          margin-right: 0;
          text-align: left;
        }
      }

      .search-filter-content {
        flex: 1;
        display: flex;
        align-items: center;
        gap: 8px;

        @include mobile {
          width: 100%;
          flex-wrap: wrap;

          .ant-select,
          .ant-range-picker,
          .search-input-container {
            width: 100% !important;
            max-width: 100% !important;
          }
        }

        &.search-keyword-area {
          display: flex;
          gap: 8px;

          @include mobile {
            flex-direction: column;
            width: 100%;

            .search-input-container {
              width: 100% !important;
            }

            .search-btn-filter,
            .init-btn {
              width: 100%;
            }
          }
        }
      }
    }
  }

  .search-btn-filter {
    height: 36px;
    padding: 0 20px;
    background-color: #2F3337;
    color: white;
    border: none;
    border-radius: 6px;
    font-size: 14px;
    cursor: pointer;
    white-space: nowrap;

    &:hover {
      background-color: #1a1c1e;
    }
  }

  .init-btn {
    height: 36px;
    padding: 0 20px;
    background-color: #fff;
    color: #666;
    border: 1px solid #d9d9d9;
    border-radius: 6px;
    font-size: 14px;
    cursor: pointer;
    white-space: nowrap;

    &:hover {
      background-color: #f5f5f5;
    }
  }

  .ant-like-input {
    height: 36px;
    padding: 0 12px;
    border: 1px solid #d9d9d9;
    border-radius: 6px;
    font-size: 14px;
    transition: all 0.3s;

    &:hover {
      border-color: #ffe414;
    }

    &:focus {
      outline: none;
      border-color: #ffe414;
      box-shadow: 0 0 0 2px rgba(255, 228, 20, 0.2);
    }
  }

  .result-control-wrapper {
    margin-top: 20px;
    display: flex;
    justify-content: space-between;
    align-items: center;

    @include mobile {
      flex-direction: column;
      gap: 16px;

      .left-area {
        width: 100%;
        justify-content: space-between;
      }

      .right-area {
        width: 100%;
        flex-direction: column;
        
        button {
          width: 100%;
          justify-content: center;
        }
      }
    }

    .left-area {
      display: flex;
      align-items: center;
      gap: 15px;
    }

    .right-area {
      display: flex;
      gap: 10px;
    }
  }

  .contract-list-wrapper {
    margin-top: 20px;

    :deep(.ant-table-thead > tr > th) {
      text-align: center !important;
      background-color: #f8f9fa;
      font-weight: 500;
    }

    :deep(.ant-table-tbody > tr > td) {
      text-align: center !important;
    }

    :deep(.ant-table-cell) {
      padding: 12px !important;
    }

    :deep(.ant-btn-link) {
      padding: 4px 0;
      height: auto;
      line-height: 1;
    }

    :deep(.table-inside-btn) {
      height: 22px !important;
      padding: 0 8px !important;
      line-height: 20px !important;
      font-size: 12px !important;
    }
  }

  .pagination-wrapper {
  }
}

.modal-loading {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100px;
}

.phone-view-content {
  padding: 20px;
  text-align: center;
}

.phone-verify-container {
  padding: 20px;
  
  .verify-input-group {
    margin-bottom: 16px;
    
    .input-label {
      font-size: 14px;
      color: #333;
      margin-bottom: 8px;
      font-weight: 500;
    }
    
    .input-wrapper {
      display: flex;
      gap: 8px;
      
      .manager-phone-input {
        flex: 1;
      }
      
      .verify-btn {
        min-width: 80px;
        background-color: #ffe414;
        border-color: #ffe414;
        color: #000;
        
        &:hover {
          background-color: #ffd814;
          border-color: #ffd814;
        }
        
        &:disabled {
          background-color: #f5f5f5;
          border-color: #d9d9d9;
          color: rgba(0, 0, 0, 0.25);
        }
      }
    }
  }

  .verify-input-group {
    .verification-input {
      flex: 1;
      letter-spacing: 2px;
      font-size: 16px;
      text-align: center;
    }
  }

  .phone-display-container {
    padding: 20px;
    text-align: center;
    
    .phone-info {
      background-color: #f8f9fa;
      padding: 20px;
      border-radius: 8px;
      
      .info-label {
        font-size: 14px;
        color: #666;
        margin-bottom: 8px;
      }
      
      .info-value {
        font-size: 24px;
        font-weight: 500;
        color: #333;
      }
    }
  }
}

.custom-tabs {
  :deep(.ant-tabs-nav) {
    padding: 0 16px;
    margin-bottom: 16px;
    
    .ant-tabs-tab {
      padding: 8px 16px;
      font-size: 14px;
      
      &.ant-tabs-tab-active {
        font-weight: 500;
      }
    }
  }
}
</style> 